import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [initialName, setInitialName] = useState("");
  const [page, setPage] = useState("");
  const [ad, setAd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      setUserId(u.user.id);
      setEmail(u.user.email ?? "");
      const { data } = await supabase
        .from("profiles")
        .select("full_name, meta_page, meta_ad_account")
        .eq("id", u.user.id)
        .maybeSingle();
      if (data) {
        setName(data.full_name ?? "");
        setInitialName(data.full_name ?? "");
        setPage(data.meta_page ?? "");
        setAd(data.meta_ad_account ?? "");
      }
    })();
  }, []);

  async function saveChanges() {
    if (!userId) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: name })
      .eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Failed to save changes");
      return;
    }
    setInitialName(name);
    toast.success("Profile updated");
  }

  const dirty = name.trim() !== initialName.trim();

  return (
    <AppShell title="Settings">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h2 className="text-2xl font-semibold">Settings</h2>
          <p className="text-sm text-muted-foreground">
            Manage your account and connected ad systems.
          </p>
        </div>

        <section className="surface-panel p-6">
          <h3 className="text-base font-semibold">Account</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1.5">
              <Label htmlFor="full_name" className="text-xs uppercase tracking-wider text-muted-foreground">
                Full name
              </Label>
              <Input
                id="full_name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input id="email" value={email} disabled readOnly />
            </div>
          </div>
          <div className="mt-5">
            <Button size="sm" onClick={saveChanges} disabled={!dirty || saving || !name.trim()}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </section>

        <section className="surface-panel p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Facebook className="h-4 w-4 text-meta" /> Meta Ads
              </h3>
              <p className="text-sm text-muted-foreground">Linked Facebook Page and Ad Account.</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success">
              <CheckCircle2 className="h-3.5 w-3.5" /> Connected
            </span>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <Field label="Facebook Page" value={page || "—"} />
            <Field label="Ad Account" value={ad || "—"} />
          </div>
          <div className="mt-5">
            <Button variant="outline" size="sm" onClick={() => navigate({ to: "/onboarding" })}>
              Reconnect Meta
            </Button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
